// Dashboard Shift card (architecture sprint-13 §15). Shown when GET /api/shifts/start-options has items or a
// shift is open. Idle: one row per option, in the API's order (by project name), a location choice when the
// project asks for one, and Start Shift. On shift: the hero with elapsed time and End Shift.
//
// A running or paused timer and an open shift never coexist: Start with a timer asks "Stop your timer and
// start the shift?" and chains the ONE stop flow (TimerActionsProvider, the midnight dialog included) into the
// start; a TIMER_RUNNING answer re-opens the same prompt. End Shift is one tap, never a confirmation (sprint 19:
// the roster and its early-end prompt are gone). The API is the arbiter of every rule.
//
// Sprint 14 (architecture sprint-14 §8.4): on an alarm option, the Start click first arms the audio and plays the
// test sound (inside the gesture), asks for notification permission if undecided and shows the first-time SOP
// checklist (never blocking Start); a phone gets the "Alarms don't work on phones" warning. On shift with alarms:
// the next alarm and last check-in line, Test sound, the arm banner and the "Before your shift" link.
//
// Sprint 16 (bug fix): the SOP checklist dialog and its state live HERE, above the idle/on-shift switch. Inside
// IdleOptions it was unmounted the moment the start succeeded (the card flips to OnShift), so it closed by itself.
// A first-time request is opened when the requested project's shift appears, i.e. after the stop-timer prompt
// (and any midnight dialog) has closed, so two Radix modals never overlap. The on-shift "Before your shift" link
// and the card's phone warning open the same dialog; the /shifts/before-your-shift page stays for sharing.
import { BellRing, Clock4, MapPin, Volume2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { PhoneAlarmWarning } from '@/components/alarms/PhoneAlarmWarning';
import { SopChecklistDialog } from '@/components/alarms/SopChecklistDialog';
import { SOP_TITLE } from '@/content/shift-sop';
import { useAlarm } from '@/hooks/use-alarm';
import { useMe } from '@/hooks/use-me';
import { arm, isArmed, playTest } from '@/lib/alarm-audio';
import { acknowledgeSop, sopAcknowledged } from '@/lib/alarm-store';
import { setSoundBlocked } from '@/lib/alarm-ui-state';
import { notificationPermission, requestNotificationPermission } from '@/lib/notifications';
import { ProjectColorBar } from '@/components/ColorSwatch';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SectionTitle } from '@/components/SectionTitle';
import { useTimerActions } from '@/components/timer/TimerActionsProvider';
import { Button } from '@/components/ui/button';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useActiveShift, useEndShift, useStartOptions, useStartShift } from '@/hooks/use-shifts';
import { useTimer } from '@/hooks/use-timer';
import { ApiClientError } from '@/lib/api';
import { clockIn, LOCATION_LABELS, locationLabel, shiftElapsedSeconds } from '@/lib/shift-format';
import { formatElapsed } from '@/lib/timer-time';
import { useNowTick } from '@/lib/use-now';
import type { ShiftDto, ShiftLocationValue, ShiftSnapshot, StartOptionDto } from '@/types/shifts';

const TICK_MS = 1000;
export const STOP_TIMER_PROMPT = 'Stop your timer and start the shift?';
export const ALARMS_ON_SHIFT = 'Alarms on this shift';
export const TEST_SOUND = 'Test sound';
const GENERIC_ERROR = 'Something went wrong. Please try again.';

const LOCATION_OPTIONS = [
  { value: 'on_site', label: LOCATION_LABELS.on_site },
  { value: 'remote', label: LOCATION_LABELS.remote },
] as const;

function errorText(err: unknown): string {
  return err instanceof ApiClientError ? err.message : GENERIC_ERROR;
}

interface ShiftCardProps {
  tz: string;
  /** Sprint 15 (§9.3): the dashboard selection; the idle card offers only this project's start options. */
  projectId?: string;
}

export function ShiftCard({ tz, projectId }: ShiftCardProps): JSX.Element | null {
  const snapshot = useActiveShift();
  const options = useStartOptions();
  const items = options.data ?? [];
  const { data: me } = useMe();
  const [sopOpen, setSopOpen] = useState(false);
  // Project id of a Start that asked for the first-time checklist; it opens once that project's shift is open.
  const sopForProject = useRef<string | null>(null);
  const openShiftProjectId = snapshot?.shift?.project.id ?? null;

  useEffect(() => {
    if (openShiftProjectId !== null && sopForProject.current === openShiftProjectId) {
      sopForProject.current = null;
      setSopOpen(true);
    }
  }, [openShiftProjectId]);

  function openSop(): void {
    setSopOpen(true);
  }

  const idleItems = projectId === undefined ? items : items.filter((o) => o.project.id === projectId);

  let body: JSX.Element | null = null;
  if (snapshot?.shift) {
    body = <OnShift snapshot={snapshot} shift={snapshot.shift} tz={tz} onOpenSop={openSop} />;
  } else if (idleItems.length > 0) {
    body = (
      <IdleOptions
        items={idleItems}
        onRequestSop={(projectId) => {
          sopForProject.current = projectId;
        }}
        onOpenSop={openSop}
      />
    );
  }
  if (body === null && !sopOpen) return null;
  return (
    <>
      {body}
      <SopChecklistDialog
        open={sopOpen}
        onDone={() => {
          if (me) acknowledgeSop(me.id);
          setSopOpen(false);
        }}
      />
    </>
  );
}

// ── idle ────────────────────────────────────────────────────────────────────

interface IdleOptionsProps {
  items: StartOptionDto[];
  /** Asks for the first-time checklist once this project's shift has started. */
  onRequestSop: (projectId: string) => void;
  onOpenSop: () => void;
}

function IdleOptions({ items, onRequestSop, onOpenSop }: IdleOptionsProps): JSX.Element {
  const timer = useTimer();
  const actions = useTimerActions();
  const start = useStartShift();
  const [chosen, setChosen] = useState<Record<string, ShiftLocationValue | null>>({});
  const [confirmFor, setConfirmFor] = useState<StartOptionDto | null>(null);
  const { data: me } = useMe();

  function locationOf(o: StartOptionDto): ShiftLocationValue | null {
    return o.project.id in chosen ? (chosen[o.project.id] ?? null) : o.prefillLocation;
  }

  function doStart(o: StartOptionDto): void {
    const location = o.workLocation === 'off' ? undefined : (locationOf(o) ?? undefined);
    start.mutate(
      { projectId: o.project.id, ...(location ? { location } : {}) },
      {
        onSuccess: ({ alreadyStarted }) => toast.success(alreadyStarted ? 'You are already on shift.' : 'Shift started'),
        onError: (err) => {
          if (err instanceof ApiClientError && err.code === 'TIMER_RUNNING') {
            setConfirmFor(o);
            return;
          }
          toast.error(errorText(err));
        },
      },
    );
  }

  /** Sprint 14: must run synchronously in the click (the browser unlocks audio only inside a gesture). */
  function prepareAlarms(o: StartOptionDto): void {
    if (!o.alarmsEnabled) return;
    void playTest(me?.preferences.alarmSound ?? 'klaxon').then((ok) => setSoundBlocked(!ok));
    if (notificationPermission() === 'default') void requestNotificationPermission();
    if (me && !sopAcknowledged(me.id)) onRequestSop(o.project.id);
  }

  function onStart(o: StartOptionDto): void {
    prepareAlarms(o);
    if (timer.data) setConfirmFor(o);
    else doStart(o);
  }

  return (
    <section aria-labelledby="shift-card-title" className="mb-8">
      <SectionTitle id="shift-card-title" title="Shift" hint="Start your shift here. Shift projects don't use the timer or manual entries." />
      <div className="card space-y-1 py-2">
        {items.map((o) => {
          const location = locationOf(o);
          const missingLocation = o.workLocation === 'required' && location === null;
          return (
            <div key={o.project.id} className="flex flex-col gap-3 py-2 sm:flex-row sm:items-center" data-testid={`shift-option-${o.project.id}`}>
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <ProjectColorBar hex={o.project.color} />
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-sm text-foreground">
                    <span className="truncate">{o.project.name}</span>
                  </p>
                  {o.alarmsEnabled && (
                    <p className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <BellRing className="h-3 w-3" aria-hidden="true" />
                      {ALARMS_ON_SHIFT}
                    </p>
                  )}
                </div>
              </div>
              {o.workLocation !== 'off' && (
                <SegmentedControl
                  aria-label={`Work location for ${o.project.name}`}
                  // No segment is checked until a location is chosen (or prefilled).
                  value={(location ?? '') as ShiftLocationValue}
                  onChange={(v) => setChosen((c) => ({ ...c, [o.project.id]: v }))}
                  options={LOCATION_OPTIONS}
                />
              )}
              <Button
                onClick={() => onStart(o)}
                disabled={start.isPending || actions.pending || missingLocation}
                title={missingLocation ? 'Choose On site or Remote' : undefined}
                aria-label={`Start shift on ${o.project.name}`}
              >
                <Clock4 aria-hidden="true" /> Start Shift
              </Button>
            </div>
          );
        })}
        <PhoneAlarmWarning show={items.some((o) => o.alarmsEnabled)} onOpenSop={onOpenSop} />
      </div>
      <ConfirmDialog
        open={confirmFor !== null}
        onOpenChange={(o) => !o && setConfirmFor(null)}
        title={STOP_TIMER_PROMPT}
        body="Your timer is saved first, then the shift starts."
        confirmLabel="Stop and start"
        pending={actions.pending || start.isPending}
        onConfirm={() => {
          const target = confirmFor;
          setConfirmFor(null);
          if (target) actions.stopThen(() => doStart(target));
        }}
      />
    </section>
  );
}

// ── on shift ────────────────────────────────────────────────────────────────

interface OnShiftProps {
  snapshot: ShiftSnapshot;
  shift: ShiftDto;
  tz: string;
  onOpenSop: () => void;
}

function OnShift({ snapshot, shift, tz, onOpenSop }: OnShiftProps): JSX.Element {
  const perfNow = useNowTick(TICK_MS);
  const end = useEndShift();
  const elapsed = formatElapsed(shiftElapsedSeconds(snapshot, perfNow));
  const location = locationLabel(shift.location, shift.locationSelfDeclared);
  const alarmSnap = useAlarm();
  const alarm = alarmSnap?.alarm && alarmSnap.alarm.shiftId === shift.id ? alarmSnap.alarm : null;
  // Re-read every tick: the AudioContext state changes without a React update.
  const armed = isArmed();

  function doEnd(): void {
    end.mutate(shift.id, {
      onSuccess: () => toast.success('Shift ended'),
      onError: (err) => toast.error(errorText(err)),
    });
  }

  return (
    <section aria-labelledby="shift-hero-title" className="card mb-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <ProjectColorBar hex={shift.project.color} />
          <div className="min-w-0 space-y-1">
            <h2 id="shift-hero-title" className="text-sm font-normal text-muted-foreground">
              On shift · <span className="text-foreground">{shift.project.name}</span>
            </h2>
            <p role="timer" className="font-mono text-4xl tabular-nums text-foreground">
              {elapsed}
            </p>
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>Started {clockIn(shift.startedAt, tz)}</span>
              {location && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" aria-hidden="true" />
                  {location}
                </span>
              )}
            </p>
            {alarm && (
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-testid="alarm-line">
                <span className="inline-flex items-center gap-1">
                  <BellRing className="h-3 w-3" aria-hidden="true" />
                  {alarm.nextMarkAt ? `Next alarm ${clockIn(alarm.nextMarkAt, tz)}` : 'No more alarms this shift'}
                </span>
                {alarm.lastConfirmedAt && <span>Last check-in {clockIn(alarm.lastConfirmedAt, tz)}</span>}
                <button type="button" className="text-link underline-offset-2 hover:underline" aria-haspopup="dialog" onClick={onOpenSop}>
                  {SOP_TITLE}
                </button>
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {alarm && (
            <Button variant="outline" onClick={() => void playTest(alarm.sound).then((ok) => setSoundBlocked(!ok))}>
              <Volume2 aria-hidden="true" /> {TEST_SOUND}
            </Button>
          )}
          <Button variant="destructive" onClick={doEnd} disabled={end.isPending}>
            End Shift
          </Button>
        </div>
      </div>
      {alarm && !armed && (
        <button type="button" className="mt-4 w-full rounded-lg bg-warning px-3 py-2 text-left text-sm text-warning-fg" onClick={() => void arm()}>
          Click to arm alarms
        </button>
      )}
    </section>
  );
}
